// DUPLICATE GUARD for every outgoing WhatsApp text (Founder 2026-09-28, ticket 13556: a pro got "הגעתי מאתר
// המומחים לגבי אינסטלטור, אינסטלטור, אינסטלטור, בעיה באסלה/ניאגרה, אשדוד" — "with a guard and a validator that
// there are no duplicate words before sending"). Plain JS so BOTH the worker (scripts/wa-worker.mjs) and the
// Next app (TS, like lib/claudeTerminalCore.mjs) share ONE implementation. The guard is a safety net — the
// root fix lives in the composers; when the guard has to fix something it returns `fixed` so the caller logs
// it (he_wa_events 'msg.duplicate_fixed') and the composer bug is found instead of silently hidden.

// A comma BETWEEN DIGITS is a thousands separator ("1,000,000 ₪"), never a clause break (2026-09-28: the first
// version turned "250,000-1,000,000" into "250,000-1,000" in the dry scan — caught before shipping).
const SEP = /((?<!\d),\s*|,(?!\d)\s*|\s+[—–-]\s+|\s*·\s*)/;  // clause separators (kept via the capture group)
const KEEP_REPEAT = new Set(['מאוד', 'הרבה', 'שוב', 'עוד', 'ממש', 'very']); // "מאוד מאוד" is Hebrew emphasis, not a bug
const norm = s => String(s || '').replace(/[.!?:;"'״׳()\[\]]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();

/** Dedupe one line: a clause that already appeared EARLIER in the line (not only adjacent) is dropped with
 *  its separator; a word repeated back-to-back ("אינסטלטור אינסטלטור") collapses to one. */
function guardLine(line, fixed) {
  let out = line;
  // 1) back-to-back repeated words (≥2 letters), outside the emphasis allow-list
  // 04/10 (AC/debt answers dropped): a repeat is a doubled word when whitespace-adjacent, or after a comma only when the repeat is a WHOLE
  // clause ("אינסטלטור, אינסטלטור"); a clause that ends with the word the next one opens with ("התקנת מזגן, מזגן עילי") is two answers
  out = out.replace(/(^|\s|,\s)([^\s,.!?]{2,})((?:\s+\2|,\s+\2(?=\s*(?:[,.!?]|$)))+)(?=$|[\s,.!?])/gu, (m, pre, w, rep) => {
    if (KEEP_REPEAT.has(w) || /^[\d.,₪%-]+$/.test(w)) return m; // emphasis words and numbers are never "duplicates"
    fixed.push(`מילה כפולה: "${w}"`);
    return pre + w;
  });
  // 2) repeated clauses anywhere in the line
  // 07/10 (id 1100 cut mid-sentence): a comma INSIDE parentheses ("דניאל (יבנה, 26 ק״מ)") is part of one item, never a clause break;
  // otherwise the 3rd expert's "26 ק״מ)" looked like a repeat of the 1st and was dropped with the rest of the line.
  const MASK = '\u0001';
  const masked = out.replace(/\([^()\n]*\)/g, g => g.split(',').join(MASK));
  const tokens = masked.split(SEP).map(t => t == null ? t : t.split(MASK).join(','));
  const seen = new Set(); const parts = []; let dropped = false; let tailPunct = '';
  for (let i = 0; i < tokens.length; i += 2) {
    const clause = tokens[i]; const sep = tokens[i + 1];
    const n = norm(clause);
    if (n && n.length >= 2 && seen.has(n)) {
      fixed.push(`קטע כפול: "${clause.trim()}"`); dropped = true;
      if (sep === undefined) tailPunct = (clause.match(/[.!?]+\s*$/) || [''])[0].trim(); // keep the line's closing punctuation
      continue;
    }
    if (n) seen.add(n);
    parts.push(clause); if (sep !== undefined) parts.push(sep);
  }
  if (dropped) {
    out = parts.join('');
    // only when a clause was removed: drop the separator it left dangling at the end, restore its punctuation
    if (tailPunct) out = out.replace(/(,\s*|\s+[—–-]\s+|\s*·\s*)$/u, '').replace(/[.!?]*$/, '') + tailPunct;
    out = out.replace(/\s+([,.!?])/g, '$1').trim();
  }
  return out;
}


// PRIVACY LAW (Founder 19/10/26): what the customer SEARCHED on Google is OUR intelligence and never reaches a business owner
// ("אנחנו אף פעם לא חושפים לבעל עסק מה הלקוח חיפש בגוגל! זה היתרון שלנו, זה רק מידע בשבילנו — תתקן מהשורש").
// The ROOT fix lives in the composers (scripts/wa-worker.mjs leadFacts: the term never enters a pro-facing string and the field
// is named `termInternal`); this is the net at the SHARED door — every outbound WhatsApp text passes guardMessage, so a composer
// that re-adds the term is stripped AND reported in `fixed`, and the caller logs it instead of the leak going out silently.
const SEARCH_TERM_SRC = '(?:[\u00b7|]\\s*)?(?:\u05d4?\u05d7\u05d9\u05e4\u05d5\u05e9\\s+\u05d1\u05d2\u05d5\u05d2\u05dc|\u05d7\u05d9\u05e4\u05e9(?:\u05d4)?\\s+\u05d1\u05d2\u05d5\u05d2\u05dc|\u05de\u05d5\u05e0\u05d7\\s+\u05d4?\u05d7\u05d9\u05e4\u05d5\u05e9|\u05de\u05d9\u05dc\u05ea\\s+\u05d4?\u05d7\u05d9\u05e4\u05d5\u05e9|utm_term)\\s*[:\uff1a]?\\s*(?:["\u2018\u2019\u201c\u201d\u05f4\'][^"\u2018\u2019\u201c\u201d\u05f4\'\\n]{0,80}["\u2018\u2019\u201c\u201d\u05f4\']?|[^\\n\u00b7|\u2013\u2014-]{0,80})?';
/** Tidy what the strip left behind: collapsed separators, a dangling "–"/"·" at the end of the line, an empty bold pair. */
const tidySearchLine = l => String(l)
  .replace(/^[ \t]*(?:\u00b7|\|)[ \t]*/, '')
  .replace(/[ \t]{2,}/g, ' ')
  .replace(/[ \t]*(?:\u00b7|\|)[ \t]*(?=(?:\u00b7|\|)|\*?$)/g, '')
  .replace(/[ \t]*[\u00b7|\u2013\u2014-]+[ \t]*(?=\*?$)/g, '')
  .replace(/\*[ \t]*\*/g, '')
  .replace(/\([ \t]*\)/g, '')
  .replace(/[ \t]+([,.!?])/g, '$1')
  .trimEnd();
/** @param {string} text @returns {{ text: string, fixed: string[] }} the text with every Google-search-term mention removed. */
export function stripSearchTerm(text) {
  const fixed = [], kept = [];
  for (const line of String(text ?? '').split('\n')) {
    const re = new RegExp(SEARCH_TERM_SRC, 'gi');
    if (!re.test(line)) { kept.push(line); continue; }
    let out = line.replace(new RegExp(SEARCH_TERM_SRC, 'gi'), m => { fixed.push(`\u05de\u05d5\u05e0\u05d7 \u05d7\u05d9\u05e4\u05d5\u05e9 \u05d1\u05d2\u05d5\u05d2\u05dc \u05d4\u05d5\u05e1\u05e8: "${m.replace(/\s+/g, ' ').trim().slice(0, 60)}"`); return ''; });
    out = tidySearchLine(out);
    // the line was nothing but a label for the term ("איך הגיע:") or has no content left → the whole line goes
    if (/[:\uff1a][ \t]*\*?$/.test(out) || !/[\u05d0-\u05ea0-9A-Za-z]/.test(out)) continue;
    kept.push(out);
  }
  return { text: kept.join('\n').replace(/\n{3,}/g, '\n\n'), fixed };
}

/** The same privacy rule for a WHOLE WhatsApp payload: a template's body parameters never pass through guardMessage (Meta renders
 *  them into the approved text), so every string of the payload is stripped here. A parameter that was nothing but the term would
 *  go out empty and Meta rejects empty params → it falls back to a neutral line.
 *  @returns {{ payload: any, fixed: string[] }} */
export function stripPayloadSearchTerm(payload, { emptyFallback = 'פרטים בשיחה' } = {}) {
  const fixed = [];
  const fix = (t, fb) => { const r = stripSearchTerm(String(t ?? '')); if (!r.fixed.length) return t; fixed.push(...r.fixed); return r.text.trim() || fb; };
  const walk = v => {
    if (Array.isArray(v)) return v.map(walk);
    if (!v || typeof v !== 'object') return v;
    const o = { ...v };
    if (typeof o.text === 'string') o.text = fix(o.text, o.type === 'text' ? emptyFallback : o.text);
    if (typeof o.caption === 'string') o.caption = fix(o.caption, o.caption);
    for (const k of ['components', 'parameters', 'interactive', 'action', 'header', 'body', 'footer', 'buttons', 'sections', 'rows']) if (o[k] && typeof o[k] === 'object') o[k] = walk(o[k]);
    return o;
  };
  return { payload: walk(payload), fixed };
}

/** Validator: the Google-search-term leaks a text or payload still contains (empty = clean). Used by tests and the admin tool. */
export function findSearchTermLeaks(x) { return typeof x === 'string' ? stripSearchTerm(x).fixed : stripPayloadSearchTerm(x).fixed; }

/** @param {string} text @returns {{ text: string, fixed: string[] }} */
export function guardMessage(text) {
  const privacy = stripSearchTerm(text); // PRIVACY FIRST: the Google search term never leaves our side (Founder 19/10/26)
  const fixed = [...privacy.fixed];
  const lines = privacy.text.split('\n').map(l => (l.trim() ? guardLine(l, fixed) : l));
  return { text: lines.join('\n'), fixed };
}

/** Validator: the duplicates a text still contains (empty = clean). Used by tests and the admin test tool. */
export function findDuplicates(text) { return guardMessage(text).fixed; }
